import React, { FC, useState } from 'react';
import { Autocomplete, Button, Grid, InputAdornment, TextField } from '@mui/material';
import { useAppSelector } from '@hooks/redux-custom';
import { Product } from '@models';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
export interface BundleProductList {
  sku: string;
  productName: string;
  price: number;
  quantity: number;
  cost: number;
  weight: number;
}

type Props = {
  onSave: (productList: BundleProductList) => void;
  selectedProducts: BundleProductList[];
};

const BundleProductLists: FC<Props> = ({ onSave, selectedProducts }) => {
  const allProducts = useAppSelector((state) => state?.products?.value?.products);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [costPrice, setCostPrice] = useState(selectedProduct?.price);
  const [productQuantity, setProductQuantity] = useState(1);
  const [searchSKU, setSearchSKU] = useState('');
  const [error, setError] = useState<{ value: boolean; message: string }>({ value: false, message: '' });
  const [quantityError, setQuantityError] = useState<{ value: boolean; message: string }>({ value: false, message: '' });
  const [costError, setCostError] = useState<{ value: boolean; message: string }>({ value: false, message: '' });

  const handleProductChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCostPrice(event.target.value as unknown as number);
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => setProductQuantity(event.target.value as unknown as number);
  const handleSkuInput = (event: React.ChangeEvent<HTMLInputElement>) => setSearchSKU(event.target.value as unknown as string);

  const clearState = () => {
    setSelectedProduct(null);
    setCostPrice(0);
    setProductQuantity(1);
    setSearchSKU('');
    setQuantityError({ value: false, message: '' });
    setCostError({ value: false, message: '' });
  };

  let selectedSKUs: string[];

  if (selectedProducts) {
    selectedSKUs = selectedProducts.map((item) => item.sku);
  }

  return (
    <Grid container alignItems="center" spacing={2} paddingTop={1}>
      <Grid item xs={6}>
        <Autocomplete
          value={selectedProduct}
          filterOptions={(allItems) => {
            return allItems?.filter(
              (item) =>
                item.sku.includes(searchSKU) ||
                (item.name.toUpperCase().includes(searchSKU.toUpperCase()) && !selectedSKUs.includes(item.sku)),
            );
          }}
          options={allProducts?.filter((item) => !selectedSKUs.includes(item.sku))}
          getOptionLabel={(option) => option?.name ?? ''}
          disablePortal
          renderInput={(params) => (
            <TextField error={error.value} helperText={error.message} onChange={handleSkuInput} {...params} label={`Product`} />
          )}
          onChange={(e: any, newValue: Product | null) => {
            setError({ value: false, message: '' });
            setCostPrice(newValue?.price);
            setSelectedProduct(newValue ?? null);
          }}
          data-testid={`${selectedProduct}-bundle-product-name`}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          label={`Product Price`}
          onChange={handleProductChange}
          value={costPrice}
          type="number"
          inputProps={{ inputMode: 'numeric', pattern: '^[0-9]*(.[0-9]{0,2})?$', min: '0' }}
          error={costError.value}
          helperText={costError.message}
          data-testid={`${selectedProduct}-bundle-product-price`}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          type="number"
          onChange={handleQuantityChange}
          value={productQuantity}
          inputProps={{ inputMode: 'numeric', pattern: '[1-9]*', min: '1' }}
          label="Quantity"
          error={quantityError.value}
          helperText={quantityError.message}
          data-testid={`${selectedProduct}-bundle-product-quantity-text`}
        />
      </Grid>
      <Grid item xs={2}>
        <Button
          startIcon={<CheckBoxIcon />}
          color="primary"
          variant="contained"
          onClick={() => {
            if (!selectedProduct) {
              setError({ value: true, message: 'Select a Product' });
            } else if (productQuantity <= 0) {
              setQuantityError({ value: true, message: 'Product quantity cannot be 0 or less' });
            } else if (costPrice !== undefined && costPrice <= 0) {
              setCostError({ value: true, message: 'Product cost cannot be 0 or less' });
            } else {
              const productList: BundleProductList = {
                sku: selectedProduct?.sku ?? '',
                productName: selectedProduct?.name ?? '',
                price: costPrice ?? 0,
                quantity: productQuantity,
                cost: selectedProduct?.cost_price ?? 0,
                weight: selectedProduct?.weight ?? 0,
              };

              onSave(productList);
              clearState();
            }
          }}
          data-testid={`${selectedProduct}-bundle-product-add-btn`}
        >
          Add
        </Button>
      </Grid>
    </Grid>
  );
};

export default BundleProductLists;
